import React, { useEffect, useState } from 'react'
import FocusLock from 'react-focus-lock'
import Portal from '../Portal/Portal'
import { keyCode } from '../../util/constants'
import './Dialog.css'
import { Button, stylesButton } from '../Button/Button'

type DialogBaseProps = {
  toggleDialog: () => void
  header: string
  children?: any
  cancelButtonLabel?: string
  customClass?: string
  autoFocus?: boolean
}

type DialogWithAction = DialogBaseProps & {
  action: (onComplete: () => void) => void
  actionButtonLabel: string
  actionDisabled?: boolean
}

type DialogWithoutAction = DialogBaseProps & {
  action?: never
  actionButtonLabel?: never
  actionDisabled?: never
}

type DialogProps = DialogWithAction | DialogWithoutAction

export default function Dialog({
  toggleDialog,
  header,
  actionButtonLabel,
  cancelButtonLabel,
  customClass,
  children,
  action,
  actionDisabled,
  autoFocus,
}: DialogProps) {
  const [actionInProgress, setActionInProgress] = useState(false)
  const doCallback = (e: any) => {
    if (!action) return
    e.preventDefault()
    e.stopPropagation() // prevent form submit event
    setActionInProgress(true)
    action(() => {
      setActionInProgress(false)
    })
  }

  const keyEventListener = (event: KeyboardEvent) => {
    // Close modal when hitting the esc key
    if (event && event.code == keyCode.Escape && toggleDialog) {
      toggleDialog()
    }
  }

  useEffect(() => {
    document.addEventListener('keyup', keyEventListener)
    document.querySelector('body')?.classList.add('no-scroll')

    return () => {
      document.removeEventListener('keyup', keyEventListener)
      document.querySelector('body')?.classList.remove('no-scroll')
    }
  }, [keyEventListener])

  return (
    <Portal>
      <FocusLock autoFocus={autoFocus ?? true}>
        <div className={`react-dialog generic ${customClass ? customClass : ''}`}>
          <div className="dialog-content">
            <div className="dialog-header">
              <h3>{header}</h3>
              <button
                data-testid="dialog-close-action"
                onClick={() => toggleDialog()}
                className="dialog-close"
              >
                <span className="icon"></span>
              </button>
            </div>
            <div className="dialog-body">{children}</div>
            <div className="dialog-footer">
              <div className="button-group">
                {actionButtonLabel && (
                  <Button
                    data-testid="action-button"
                    onClick={e => doCallback(e)}
                    disabled={actionDisabled || actionInProgress}
                    className={actionDisabled || actionInProgress ? 'disabled' : ''}
                  >
                    {actionButtonLabel}
                  </Button>
                )}
                {cancelButtonLabel && (
                  <Button
                    onClick={() => toggleDialog()}
                    disabled={actionInProgress}
                    className={
                      (actionInProgress ? 'disabled' : '') +
                      (actionButtonLabel ? ' ' + stylesButton.buttonSecondary : '')
                    }
                  >
                    {cancelButtonLabel}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </FocusLock>
    </Portal>
  )
}
